import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { parseISO } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import { SubmitErrorAlert } from '@/components/Alerts';
import Button from '@/components/Button';
import Card from '@/components/Card';
import EmptyState from '@/components/EmptyState';
import LoadingState from '@/components/LoadingState';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import { Small } from '@/components/Typography';
import { TableCell, TableRow } from '@/elements/Table';
import { formatDateSimple } from '@/util/datetime';

import {
  EligibleInvoiceTimesheet,
  useAddDraftInvoiceTimesheets,
  useEligibleInvoiceTimesheets,
} from './hooks';

type Props = {
  invoiceId: string;
  hideModal: () => void;
  onSuccess?: () => void | Promise<unknown>;
};

const formatMaybeDate = (value: string | null) =>
  value ? formatDateSimple(parseISO(value)) : 'N/A';

const AddTimesheetsModal = ({ invoiceId, hideModal, onSuccess }: Props) => {
  const { data, loading, error, fetch, clear } = useEligibleInvoiceTimesheets();
  const addTimesheets = useAddDraftInvoiceTimesheets();
  const [selectedTimesheetIds, setSelectedTimesheetIds] = useState<number[]>(
    [],
  );

  useEffect(() => {
    void fetch(invoiceId);

    return () => {
      clear();
      addTimesheets.clearError();
    };
  }, [addTimesheets.clearError, clear, fetch, invoiceId]);

  useEffect(() => {
    setSelectedTimesheetIds([]);
  }, [data?.timesheets]);

  const timesheets = data?.timesheets || [];

  const allSelected = useMemo(
    () =>
      timesheets.length > 0 &&
      selectedTimesheetIds.length === timesheets.length,
    [selectedTimesheetIds.length, timesheets.length],
  );

  const toggleTimesheet = (timesheetId: number) => {
    setSelectedTimesheetIds((current) =>
      current.includes(timesheetId)
        ? current.filter((value) => value !== timesheetId)
        : [...current, timesheetId],
    );
  };

  const toggleSelectAll = () => {
    setSelectedTimesheetIds(
      allSelected ? [] : timesheets.map((timesheet) => timesheet.id),
    );
  };

  const handleSubmit = async () => {
    const result = await addTimesheets.add({
      invoiceId,
      timesheetIds: selectedTimesheetIds,
    });

    if (!result.success) return;

    if (onSuccess) {
      await onSuccess();
    }

    hideModal();
  };

  const renderTableRow = (timesheet: EligibleInvoiceTimesheet) => {
    const isSelected = selectedTimesheetIds.includes(timesheet.id);

    return (
      <TableRow key={timesheet.id} selected={isSelected}>
        <TableCell>
          <input
            aria-label={`Select timesheet ${timesheet.id}`}
            checked={isSelected}
            type="checkbox"
            onChange={() => toggleTimesheet(timesheet.id)}
          />
        </TableCell>
        <TableCell>{timesheet.id}</TableCell>
        <TableCell>{timesheet.worker_name || 'Unassigned'}</TableCell>
        <TableCell>{timesheet.skill_name || 'N/A'}</TableCell>
        <TableCell>{timesheet.order_id}</TableCell>
        <TableCell>{timesheet.job_id}</TableCell>
        <TableCell>
          {formatMaybeDate(timesheet.first_shift_starts_at)}
        </TableCell>
        <TableCell>{formatMaybeDate(timesheet.approved_at)}</TableCell>
      </TableRow>
    );
  };

  return (
    <Modal
      disableClickout
      size="xl"
      title="Add Approved Timesheets"
      wrapperBackground={true}
      onRequestClose={hideModal}
    >
      <Card.Section>
        {error && <SubmitErrorAlert description={error} />}
        {addTimesheets.error && (
          <SubmitErrorAlert description={addTimesheets.error} />
        )}
        <Small color="light">
          Only approved, invoiceable timesheets that are not already linked to
          an invoice and are not held by an invoice are shown.
        </Small>
      </Card.Section>

      <Card.Section>
        <Stack justify="apart">
          <Small>{selectedTimesheetIds.length} selected</Small>
          {timesheets.length > 0 && (
            <Button
              a11yLabel={
                allSelected ? 'Clear all timesheets' : 'Select all timesheets'
              }
              appearance="plain"
              label={allSelected ? 'Clear All' : 'Select All'}
              type="button"
              onClick={toggleSelectAll}
            />
          )}
        </Stack>
      </Card.Section>

      <Card.Section>
        {loading ? (
          <LoadingState css={{ minHeight: 180 }} />
        ) : timesheets.length === 0 ? (
          <EmptyState
            icon={faFileAlt}
            text="No eligible approved timesheets were found for this draft invoice."
            title="No Timesheets"
          />
        ) : (
          <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
            <Table>
              <TableHeader
                fields={[
                  { name: '', style: { width: 56 } },
                  { name: 'Timesheet', style: { width: 100 } },
                  { name: 'Worker', style: { width: 180 } },
                  { name: 'Skill', style: { width: 180 } },
                  { name: 'Order', style: { width: 90 } },
                  { name: 'Job', style: { width: 90 } },
                  { name: 'Shift Starts', style: { width: 140 } },
                  { name: 'Approved At', style: { width: 140 } },
                ]}
                headerBgColor="#E0DFDF"
                headerColor="black"
              />
              <tbody>{timesheets.map(renderTableRow)}</tbody>
            </Table>
          </div>
        )}
      </Card.Section>

      <Card.Footer>
        <Stack justify="end">
          <Button
            a11yLabel="Cancel add timesheets"
            appearance="secondary"
            label="Cancel"
            type="button"
            onClick={hideModal}
          />
          <Button
            a11yLabel="Add selected timesheets"
            disabled={
              selectedTimesheetIds.length === 0 || timesheets.length === 0
            }
            isLoading={addTimesheets.loading}
            label="Add Selected Timesheets"
            type="button"
            onClick={handleSubmit}
          />
        </Stack>
      </Card.Footer>
    </Modal>
  );
};

export default AddTimesheetsModal;
